import type { TFunction } from 'i18next';

import type { MemberRole } from './types';

/** i18n keys of the six standard roles (identifiers are stable, names are translated). */
export const ROLE_KEYS: Record<MemberRole, `roles.${MemberRole}`> = {
  owner: 'roles.owner',
  lead_moderator: 'roles.lead_moderator',
  moderator: 'roles.moderator',
  editor: 'roles.editor',
  commenter: 'roles.commenter',
  member: 'roles.member',
};

export function roleLabelKey(role: MemberRole): `roles.${MemberRole}` {
  return ROLE_KEYS[role];
}

/** The ONE workspace role of a member: a standard role or a custom one. */
export interface WorkspaceRoleChoice {
  role: MemberRole | null;
  customRoleId: string | null;
}

/** ONE role in a request body: a standard ``role`` or a ``custom_role_id`` (boards: ``role``). */
export type RoleInput = { role: MemberRole } | { custom_role_id: string };

export function toRoleInput(choice: WorkspaceRoleChoice): RoleInput {
  return choice.customRoleId !== null
    ? { custom_role_id: choice.customRoleId }
    : { role: choice.role ?? 'member' };
}

export const STANDARD_PREFIX = 'standard:';
export const CUSTOM_PREFIX = 'custom:';

export function roleChoiceValue(choice: WorkspaceRoleChoice): string | null {
  if (choice.customRoleId !== null) return `${CUSTOM_PREFIX}${choice.customRoleId}`;
  if (choice.role !== null) return `${STANDARD_PREFIX}${choice.role}`;
  return null;
}

/** The role of an invitation / link: a translated standard role or a custom role's name. */
export function grantedRoleLabel(
  t: TFunction,
  role: MemberRole | null,
  customRoleName: string | null,
): string {
  if (role !== null) return t(ROLE_KEYS[role]);
  return customRoleName ?? t('roles.custom');
}
